import type { AnswerValue, FormTheme, Question } from '@/types/form'
import { Suspense } from 'react'
import { DateInput } from './Inputs/DateInput'
import { DividerBlock } from './Inputs/DividerBlock'
import { DropdownInput } from './Inputs/DropdownInput'
import { FileUploadInput } from './Inputs/FileUploadInput'
import { IntroBlock } from './Inputs/IntroBlock'
import { LongTextInput } from './Inputs/LongTextInput'
import { MatrixInput } from './Inputs/MatrixInput'
import { MultipleChoiceInput } from './Inputs/MultipleChoiceInput'
import { NPSInput } from './Inputs/NPSInput'
import { NestedSelectInput } from './Inputs/NestedSelectInput'
import { NumberInput } from './Inputs/NumberInput'
import { NumberSliderInput } from './Inputs/NumberSliderInput'
import { OpinionScaleInput } from './Inputs/OpinionScaleInput'
import { PhoneInput } from './Inputs/PhoneInput'
import { PictureChoiceInput } from './Inputs/PictureChoiceInput'
import { RankingInput } from './Inputs/RankingInput'
import { RatingInput } from './Inputs/RatingInput'
import { SectionTitleBlock } from './Inputs/SectionTitleBlock'
import { ShortTextInput } from './Inputs/ShortTextInput'
import { SignatureInput } from './Inputs/SignatureInput'
import { TaiwanAddressInput } from './Inputs/TaiwanAddressInput'
import { TaiwanLandlineInput } from './Inputs/TaiwanLandlineInput'
import { TimeInput } from './Inputs/TimeInput'
import { TotalInput } from './Inputs/TotalInput'
import { YesNoInput } from './Inputs/YesNoInput'

export interface AnswerInputProps<T = AnswerValue> {
  question: Question
  value: T | undefined
  answers?: Map<string, AnswerValue>
  onChange: (value: T) => void
  onSubmit: () => void
  autoFocus?: boolean
  showInlineSubmit?: boolean
  disabled?: boolean
  loadingMessage?: string | null
  theme: FormTheme
}

interface QuestionRendererProps extends AnswerInputProps {
  value: AnswerValue | undefined
}

export function QuestionRenderer(props: QuestionRendererProps) {
  const { question } = props

  return (
    <Suspense
      fallback={
        <div className="text-sm" style={{ color: 'var(--form-answer,#64748B)' }}>
          載入題型...
        </div>
      }
    >
      {question.type === 'LONG_TEXT' && (
        <LongTextInput {...props} value={props.value as string | undefined} />
      )}
      {['SHORT_TEXT', 'EMAIL', 'WEBSITE', 'TAIWAN_MOBILE'].includes(question.type) && (
        <ShortTextInput {...props} value={props.value as string | undefined} />
      )}
      {question.type === 'PHONE_NUMBER' && (
        <PhoneInput {...props} value={props.value as string | undefined} />
      )}
      {question.type === 'TAIWAN_LANDLINE' && (
        <TaiwanLandlineInput {...props} value={props.value as string | undefined} />
      )}
      {question.type === 'TAIWAN_ADDRESS' && <TaiwanAddressInput {...props} />}
      {question.type === 'MULTIPLE_CHOICE' && (
        <MultipleChoiceInput {...props} value={props.value as string | string[] | undefined} />
      )}
      {question.type === 'CHECKBOXES' && (
        <MultipleChoiceInput
          {...props}
          value={props.value as string | string[] | undefined}
          multiple
        />
      )}
      {question.type === 'DROPDOWN' && (
        <DropdownInput {...props} value={props.value as string | undefined} />
      )}
      {question.type === 'PICTURE_CHOICE' && (
        <PictureChoiceInput {...props} value={props.value as string | string[] | undefined} />
      )}
      {question.type === 'YES_NO' && (
        <YesNoInput {...props} value={props.value as boolean | undefined} />
      )}
      {question.type === 'RATING' && (
        <RatingInput {...props} value={props.value as number | undefined} />
      )}
      {question.type === 'NPS' && <NPSInput {...props} value={props.value as number | undefined} />}
      {question.type === 'DATE' && (
        <DateInput {...props} value={props.value as string | undefined} />
      )}
      {question.type === 'TIME' && (
        <TimeInput {...props} value={props.value as string | undefined} />
      )}
      {question.type === 'NUMBER' && (
        <NumberInput {...props} value={props.value as number | undefined} />
      )}
      {question.type === 'NUMBER_SLIDER' && (
        <NumberSliderInput {...props} value={props.value as number | undefined} />
      )}
      {question.type === 'TOTAL' && (
        <TotalInput {...props} value={props.value as number | undefined} />
      )}
      {question.type === 'NESTED_SELECT' && (
        <NestedSelectInput
          {...props}
          value={props.value as Record<string, string | null> | undefined}
        />
      )}
      {question.type === 'FILE_UPLOAD' && (
        <FileUploadInput {...props} value={props.value as string[] | undefined} />
      )}
      {question.type === 'SIGNATURE' && (
        <SignatureInput {...props} value={props.value as string | undefined} />
      )}
      {question.type === 'RANKING' && (
        <RankingInput {...props} value={props.value as string[] | undefined} />
      )}
      {question.type === 'MATRIX' && (
        <MatrixInput
          {...props}
          value={props.value as Record<string, string | string[]> | undefined}
        />
      )}
      {question.type === 'OPINION_SCALE' && (
        <OpinionScaleInput {...props} value={props.value as number | undefined} />
      )}
      {question.type === 'STATEMENT' && <IntroBlock {...props} value={null} />}
      {question.type === 'SECTION_TITLE' && <SectionTitleBlock {...props} value={null} />}
      {question.type === 'INTRO_BLOCK' && <IntroBlock {...props} value={null} />}
      {question.type === 'DIVIDER' && <DividerBlock {...props} value={null} />}
    </Suspense>
  )
}
